/// <reference lib="dom" />
/**
 * webEntityDraw.web.ts
 * Canvas2D draw helpers for game entities: enemies, projectiles, hit effects,
 * Nova, and particles. Owns per-entity animation state (module-level maps).
 *
 * Extracted from GameCanvas.web.tsx to keep both files under the 300-line limit.
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 * Hit-effect elapsed ticking and pruning lives in the engine (tickHitEffects).
 *
 * @module webEntityDraw
 */

import { gameState } from '../engine/gameState';
import { activeParticles } from '../engine/particleSystem';
import { getHitEffectPool } from '../engine/hitEffectVfx';
import {
  drawNovaSprite,
  drawEnemySprite,
  drawSpriteFrame,
  getNormalizedSheet,
  type AnimState,
} from './webSpriteDraw';
import { LASER_HIT_SHEET, LASER_HIT_CLIPS } from './SpriteRenderer';
import type { RenderAssets } from './RenderAssets';
import { getActiveCharacter, getActiveCharacterId } from '../data/activeCharacter';
import type { CharacterId } from '../types/character';

// ── Per-entity animation state (module-level, persists across renders) ──────

const _novaAnim: AnimState = { frameIndex: 0, elapsed: 0 };
/** Tracks the previous Nova state so we can reset elapsed on state change. */
let _novaLastState: string = 'IDLE';
const _enemyAnims = new Map<number, AnimState>();
/** Tracks each enemy's previous state for animation reset on state change. */
const _enemyLastStates = new Map<number, string>();

/**
 * Reset all per-entity animation state. Call on level load/restart to prevent
 * unbounded growth of stale map entries across level transitions.
 */
export function resetEntityDrawState(): void {
  _novaAnim.frameIndex = 0;
  _novaAnim.elapsed = 0;
  _novaLastState = 'IDLE';
  _enemyAnims.clear();
  _enemyLastStates.clear();
}

function getOrCreateEnemyAnim(id: number): AnimState {
  let anim = _enemyAnims.get(id);
  if (!anim) {
    anim = { frameIndex: 0, elapsed: 0 };
    _enemyAnims.set(id, anim);
  }
  return anim;
}

// DECISION: Source-rect crop for the 48×48 laser sprite. The glow content sits
// in roughly x=[4,44] y=[12,38]. Drawing only this interior avoids dark
// edge/outline pixels that compress into a visible line when the sprite is
// downscaled to 12×10 destination px.
const LASER_SX = 4;
const LASER_SY = 12;
const LASER_SW = 40;
const LASER_SH = 26;

// DECISION: Draw size 32×37 — preserves cropped frame aspect ratio (362×418).
const HIT_DRAW_W = 32;
const HIT_DRAW_H = Math.round(HIT_DRAW_W * (418 / 362));

/**
 * Draw all game entities: enemies, projectiles, hit effects, Nova, and particles.
 * Call once per frame after tile/background layers and before the finale overlay.
 *
 * @param ctx   - Canvas 2D rendering context.
 * @param assets - Loaded render assets.
 * @param dt    - Delta time this frame (seconds, capped).
 * @param camX  - Rounded camera X.
 * @param camY  - Rounded camera Y.
 */
export function drawEntities(
  ctx: CanvasRenderingContext2D,
  assets: RenderAssets,
  dt: number,
  camX: number,
  camY: number,
): void {
  const { nova } = gameState;

  // ── Enemies ──────────────────────────────────────────────────────────────
  for (const enemy of gameState.enemies) {
    const anim = getOrCreateEnemyAnim(enemy.id);
    // DECISION: Reset animation elapsed on state change so the new clip
    // starts at frame 0 instead of inheriting a stale elapsed counter.
    const prevEState = _enemyLastStates.get(enemy.id);
    if (prevEState !== undefined && prevEState !== enemy.state) {
      anim.elapsed = 0;
      anim.frameIndex = 0;
    }
    _enemyLastStates.set(enemy.id, enemy.state);
    drawEnemySprite(
      ctx, enemy, anim, dt, camX, camY,
      assets.nibblerImg, assets.thornhopImg, assets.gloomBatImg,
    );
  }

  // ── Projectiles ──────────────────────────────────────────────────────────
  ctx.fillStyle = '#f5c842';
  for (const shard of gameState.projectiles) {
    if (assets.laserPulseImg) {
      const pw = shard.width + 4;
      const ph = shard.height + 2;
      ctx.save();
      if (shard.vx < 0) {
        ctx.translate(Math.round(shard.x - camX + pw / 2), Math.round(shard.y - camY));
        ctx.scale(-1, 1);
        ctx.drawImage(assets.laserPulseImg, LASER_SX, LASER_SY, LASER_SW, LASER_SH, -pw / 2, 0, pw, ph);
      } else {
        ctx.drawImage(assets.laserPulseImg, LASER_SX, LASER_SY, LASER_SW, LASER_SH, Math.round(shard.x - camX - 2), Math.round(shard.y - camY - 1), pw, ph);
      }
      ctx.restore();
    } else {
      ctx.fillRect(shard.x - camX, shard.y - camY, shard.width, shard.height);
    }
  }

  // ── Laser hit effects (read-only — engine ticks elapsed and prunes) ─────
  const hitClip = LASER_HIT_CLIPS.BURST;
  const hitSheet = getNormalizedSheet(
    assets.laserHitImg, LASER_HIT_SHEET.frameWidth, LASER_HIT_SHEET.frameHeight,
  );
  const hitPool = getHitEffectPool();
  for (let hi = 0; hi < hitPool.length; hi++) {
    const fx = hitPool[hi];
    if (!fx.active) continue;
    // DECISION: Derive frameIndex from elapsed using the clip definition.
    // No mutation — elapsed is ticked by stepHitEffects() in the engine.
    const frameIndex = Math.min(
      Math.floor(fx.elapsed * hitClip.fps) + hitClip.start,
      hitClip.end,
    );
    drawSpriteFrame(
      ctx, hitSheet,
      LASER_HIT_SHEET.frameWidth, LASER_HIT_SHEET.frameHeight,
      frameIndex,
      Math.round(fx.x - camX - HIT_DRAW_W / 2),
      Math.round(fx.y - camY - HIT_DRAW_H / 2),
      HIT_DRAW_W, HIT_DRAW_H, true,
    );
  }

  // ── Nova ─────────────────────────────────────────────────────────────────
  // DECISION: Reset animation elapsed when Nova's state changes so
  // the new clip starts cleanly at frame 0, preventing visible jitter
  // from a stale elapsed counter producing a mid-clip frame index.
  if (_novaLastState !== nova.state) {
    _novaAnim.elapsed = 0;
    _novaAnim.frameIndex = 0;
    _novaLastState = nova.state;
  }
  // Hurt flash: blink by hiding Nova on every other 4-frame window.
  // DECISION: Guard with hurtFlash so stomp-bounce invincibility
  // (which also sets invincibilityFrames) does not trigger the blink.
  const hurtBlink = nova.hurtFlash && nova.invincibilityFrames > 0 && Math.floor(nova.invincibilityFrames / 4) % 2 === 0;
  // DECISION: Select sprite sheet image + metadata based on active character.
  // Character selection is read once per frame from the activeCharacter module.
  const charId: CharacterId = getActiveCharacterId();
  const charMeta = getActiveCharacter();
  const charImg = charId === 'avon' ? assets.avonImg
    : charId === 'belle' ? assets.belleImg
    : assets.novaImg;
  drawNovaSprite(ctx, charImg, nova, _novaAnim, dt, camX, camY, !hurtBlink, charMeta.sheet, charMeta.clips, charMeta.drawOffset);

  // ── Particles ────────────────────────────────────────────────────────────
  for (let pi = 0; pi < activeParticles.length; pi++) {
    const p = activeParticles[pi];
    const alpha = p.life / p.maxLife;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color;
    ctx.fillRect(p.x - camX, p.y - camY, p.size, p.size);
  }
  ctx.globalAlpha = 1;

  // DECISION: Prune stale animation state for destroyed enemies every frame.
  // Enemy count is small (≤20 per screen) so a Set lookup per map entry is
  // negligible, and prevents unbounded growth across level restarts.
  if (_enemyAnims.size > gameState.enemies.length) {
    const liveIds = new Set<number>(gameState.enemies.map(e => e.id));
    for (const key of _enemyAnims.keys()) {
      if (!liveIds.has(key)) {
        _enemyAnims.delete(key);
        _enemyLastStates.delete(key);
      }
    }
  }
}
